import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import config from '../config';

const PledgeClaimPage = ({ claimCodeProp }) => {
  const { claimCode: claimCodeParam } = useParams();
  const claimCode = claimCodeProp || claimCodeParam;
  const navigate = useNavigate();
  const [pledge, setPledge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRemaining, setTimeRemaining] = useState('');

  useEffect(() => {
    fetchPledgeByClaimCode();
  }, [claimCode]);

  useEffect(() => {
    if (pledge?.expires_at) {
      const timer = setInterval(() => {
        const now = new Date();
        const expiresAt = new Date(pledge.expires_at);
        const diff = expiresAt - now;

        if (diff <= 0) {
          setTimeRemaining('Expired');
          clearInterval(timer);
        } else {
          const days = Math.floor(diff / (1000 * 60 * 60 * 24));
          const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          
          if (days > 0) {
            setTimeRemaining(`Expires in ${days} day${days > 1 ? 's' : ''}`);
          } else {
            setTimeRemaining(`Expires in ${hours} hour${hours > 1 ? 's' : ''}`);
          }
        }
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [pledge]);

  const fetchPledgeByClaimCode = async () => {
    try {
      setLoading(true);
      const API_URL = config.API_URL;
      const response = await fetch(`${API_URL}/pledges/claim/${claimCode}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('Pledge not found or has expired');
        } else {
          setError('Failed to load pledge information');
        }
        setLoading(false);
        return;
      }

      const data = await response.json();
      setPledge(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching pledge:', err);
      setError('Failed to load pledge information');
      setLoading(false);
    }
  };

  const handleSetupShul = () => {
    // Navigate to name claim signup flow with pledge type parameter
    const recipientNameForUrl = formatRecipientNames(pledge.prayer_recipient_name);
    navigate(`/name-claim-signup?claim=${claimCode}&name=${encodeURIComponent(recipientNameForUrl)}&type=pledge`);
  };

  // Format prayer recipient name(s)
  const formatRecipientNames = (recipientName) => {
    if (!recipientName) return 'General Donation';
    
    try {
      // Try to parse as JSON array
      const names = JSON.parse(recipientName);
      if (Array.isArray(names) && names.length > 0) {
        const firstName = names[0];
        if (names.length > 1) {
          return `Dedicated to ${firstName} +${names.length - 1}`;
        }
        return `Dedicated to ${firstName}`;
      }
    } catch (e) {
      // Not JSON, treat as single name
      return `Dedicated to ${recipientName}`;
    }
    
    return 'General Donation';
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f9fafb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e5e7eb',
            borderTop: '4px solid #8b5cf6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto'
          }}></div>
          <p style={{ marginTop: '16px', color: '#6b7280' }}>Loading pledge...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f9fafb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          maxWidth: '480px',
          width: '100%',
          background: '#fff',
          borderRadius: '12px',
          padding: '32px',
          textAlign: 'center',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            background: '#fee2e2',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            fontSize: '32px'
          }}>
            ⚠️
          </div>
          <h1 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#000',
            marginBottom: '12px'
          }}>
            Unable to Load Pledge
          </h1>
          <p style={{ fontSize: '16px', color: '#6b7280', marginBottom: '24px' }}>
            {error}
          </p>
          <button
            onClick={() => window.location.href = '/'}
            style={{
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: '500',
              color: '#fff',
              background: '#8b5cf6',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f9fafb',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        maxWidth: '560px',
        width: '100%'
      }}>
        {/* Yesod Logo Header */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
          <img 
            src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Yesod%20Logo%204K%20(1).png" 
            alt="Yesod Logo" 
            style={{ height: '60px', width: 'auto' }}
          />
        </div>

        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '40px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          {/* User Profile Section */}
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{
              width: '80px',
              height: '80px',
              background: pledge.avatar_url ? 'transparent' : '#8b5cf6',
              backgroundImage: pledge.avatar_url ? `url(${pledge.avatar_url})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              fontSize: '32px',
              fontWeight: 'bold',
              color: '#fff'
            }}>
              {!pledge.avatar_url && (
                pledge.user_first_name 
                  ? pledge.user_first_name.charAt(0).toUpperCase() 
                  : '?'
              )}
            </div>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#000',
              marginBottom: '4px'
            }}>
              {(pledge.user_first_name !== null && pledge.user_first_name !== undefined) || (pledge.user_last_name !== null && pledge.user_last_name !== undefined)
                ? `${pledge.user_first_name || ''} ${pledge.user_last_name || ''}`.trim()
                : 'Anonymous'}
            </h2>
            <p style={{ fontSize: '16px', color: '#6b7280' }}>
              Made a pledge to your Shul
            </p>
          </div>

        {/* Pledge Card */}
        <div style={{
          background: '#ffffff',
          border: '2px solid #d1d5db',
          borderRadius: '12px',
          padding: '32px 24px',
          marginBottom: '20px'
        }}>
          {/* Action Required Badge */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: '#fef3c7',
            border: '1px solid #fde047',
            borderRadius: '20px',
            padding: '6px 12px',
            fontSize: '13px',
            fontWeight: '500',
            color: '#92400e',
            marginBottom: '24px'
          }}>
            🟡 Action Required
          </div>

          {/* Pledge Amount - HERO */}
          <div style={{ textAlign: 'center', marginBottom: '12px' }}>
            <div style={{
              fontSize: '56px',
              fontWeight: 'bold',
              color: '#000',
              marginBottom: '12px',
              lineHeight: '1'
            }}>
              ${parseFloat(pledge.pledge_amount).toFixed(2)}
            </div>
            <p style={{
              fontSize: '18px',
              color: '#6b7280',
              fontWeight: '500',
              marginBottom: '16px'
            }}>
              {formatRecipientNames(pledge.prayer_recipient_name)}
            </p>
            
            {/* Expiration Timer */}
            <div style={{
              fontSize: '14px',
              color: '#6b7280'
            }}>
              Expires in 48 hours
            </div>
          </div>
        </div>

        {/* Context Text - Shortened */}
        <p style={{
          fontSize: '16px',
          color: '#374151',
          textAlign: 'center',
          lineHeight: '1.6',
          marginBottom: '24px'
        }}>
          Verify your community to accept this payment.
        </p>

        {/* CTA Button */}
        <button
          onClick={handleSetupShul}
          style={{
            width: '100%',
            padding: '16px',
            fontSize: '18px',
            fontWeight: '600',
            color: '#fff',
            background: '#8b5cf6',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            marginBottom: '16px',
            transition: 'background 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.background = '#7c3aed'}
          onMouseLeave={(e) => e.target.style.background = '#8b5cf6'}
        >
          Setup Shul & Accept Pledge
        </button>

          {/* Reference Number */}
          <p style={{
            textAlign: 'center',
            fontSize: '13px',
            color: '#9ca3af'
          }}>
            Ref #{claimCode}
          </p>
        </div>

        {/* Powered by Yesod Footer */}
        <div style={{
          textAlign: 'center',
          marginTop: '20px',
          fontSize: '14px',
          color: '#6b7280'
        }}>
          Powered by <strong>Yesod</strong>
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default PledgeClaimPage;
